/**
 * Bounds and constants for the structural rules of an OVD file.
 *
 * No dependency on `@tedivo/tedivo-bay-grid-pure`: this folder is a candidate to move
 * into the `open-vessel-definition` package. See DECISIONS.md (D4, D9).
 */

export { CONTAINER_LENGTHS } from "open-vessel-definition";

export const OVD_BOUNDS = {
  /** Rows are `00`..`50`. `00` is the centerline, odd rows starboard, even rows port */
  ROW_MIN: 0,
  ROW_MAX: 50,
  /** Tiers are even. Below deck `02`..`66`, above deck `68`..`120` (3-digit tiers allowed) */
  BELOW_TIER_MIN: 2,
  BELOW_TIER_MAX: 66,
  ABOVE_TIER_MIN: 68,
  ABOVE_TIER_MAX: 120,
  /** Bays are odd, `001`..`197`. Bay 199 is excluded although the OVD pattern allows it */
  BAY_MIN: 1,
  BAY_MAX_EXCLUSIVE: 199,
} as const;

export const OVD_VERSION_RE = /^1\.\d{1,2}\.\d{1,2}$/;

export const OVD_SCHEMA_IDS: readonly string[] = [
  "OpenVesselDefinition",
  "IOpenVesselDefinitionV1",
];

export const OVD_ROOT_KEYS: readonly string[] = [
  "$schema",
  "$schemaId",
  "version",
  "sizeSummary",
  "shipData",
  "baysData",
  "positionLabels",
  "lidData",
  "vesselPartsData",
];

/** Fields that only exist while a STAF file is being converted and must not reach an OVD */
export const STAF_INTERMEDIATE_SLOT_FIELDS: readonly string[] = ["position", "acceptsSize"];
export const STAF_INTERMEDIATE_ROW_FIELDS: readonly string[] = [
  "acceptsSize",
  "topIsoTier",
  "bottomIsoTier",
];
export const STAF_INTERMEDIATE_BAY_LEVEL_FIELDS: readonly string[] = [
  "perTierInfo",
  "topIsoTier",
  "bottomIsoTier",
  "maxHeight",
  "tiersLabelsDictionary",
  "rowsLabelsDictionary",
];

/** Bay-level fields that are a `ForeAftEnum` (`1 | 2`) when present */
export const BAY_LEVEL_FORE_AFT_FIELDS = ["pairedBay", "reeferPlugs", "doors"] as const;

/** Bay-level flags that are `0 | 1` when present */
export const BAY_LEVEL_YES_NO_FLAGS: readonly string[] = [
  "centerLineRow",
  "athwartShip",
  "foreHatch",
  "ventilated",
  "heatSrcFore",
  "ignitionSrcFore",
  "quartersFore",
  "engineRmBulkFore",
  "telescoping",
];

/**
 * Plausibility bounds for values a validator cannot prove wrong but no vessel has.
 *
 * `MAX_ROW_WEIGHT_G`: weights are in grams. A stack never weighs more than a few
 * hundred tonnes; STAF sources write `32769` (an int16 overflow) where the weight is
 * unknown, which the converter turns into `3276900000`.
 */
export const OVD_PLAUSIBILITY = {
  MAX_ROW_WEIGHT_G: 1_000_000_000,
} as const;
